import { CalendarCheck, Microscope, HeartPulse, MessageCircle } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const WHATSAPP_NUMBER = '5575981872440';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, vim do site de nódulo e gostaria de agendar uma consulta com dr. Mateus')}`;

const steps = [
  {
    number: '01',
    icon: CalendarCheck,
    title: 'Consulta Inicial e Avaliação dos Exames',
    color: '#0D47A1',
    details: [
      'Você agenda a consulta',
      'Leva todos os exames (tomografia, radiografia, ressonância — tudo que tem)',
      'Dr. Mateus avalia os achados com cuidado e discute características do nódulo',
      'Alinha expectativas e define os próximos passos',
    ],
    result: 'Diagnóstico preliminar e plano de ação personalizado',
  },
  {
    number: '02',
    icon: Microscope,
    title: 'Biópsia (se indicada)',
    color: '#00897B',
    details: [
      'Dr. Mateus decidirá em conjunto com você sobre a indicação de biópsia',
      'Se indicada, será definida a melhor técnica conforme localização, tamanho e comorbidades',
      'Pode ser: biópsia percutânea por agulha, biópsia por broncoscopia ou biópsia cirúrgica',
      'Acompanhado de perto no pós-procedimento',
    ],
    result: 'Resultado histopatológico em 5-7 dias úteis',
  },
  {
    number: '03',
    icon: HeartPulse,
    title: 'Após o Diagnóstico: Plano de Tratamento',
    color: '#0D47A1',
    details: [
      'Se benigno → acompanhamento periódico ou encerramento do caso',
      'Se maligno → discussão de opções (cirurgia para ressecção ou tratamento sistêmico)',
      'Sempre em conjunto com oncologia quando necessário',
    ],
    result: 'Durante toda essa jornada você tem Dr. Mateus e toda a sua equipe ao seu lado',
  },
];

export default function NoduloJourney() {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      id="oque-fazer"
      ref={ref}
      className={`py-24 md:py-32 bg-white transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#00897B]/10 text-[#00897B] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Sua Jornada de Cuidado
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            Você Descobriu um Nódulo.<br />
            <span className="text-[#0D47A1]">Respire Fundo. Vamos Estruturar Isso.</span>
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            O primeiro sentimento quando descobre um nódulo é pânico. É normal. Mas descobrir um nódulo
            é uma oportunidade de vigilância, não uma sentença. Aqui está o caminho que você vai seguir
            com Dr. Mateus:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={i}
                className="relative bg-[#F5F5F5] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
              >
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 -right-3 w-6 h-0.5 bg-gray-300 z-10" />
                )}

                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${step.color}15` }}
                  >
                    <Icon size={22} style={{ color: step.color }} />
                  </div>
                  <span
                    className="font-poppins font-bold text-3xl"
                    style={{ color: `${step.color}25` }}
                  >
                    {step.number}
                  </span>
                </div>

                <h3 className="font-poppins font-bold text-lg text-[#212121] mb-4">{step.title}</h3>

                <ul className="space-y-2 mb-5">
                  {step.details.map((detail, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0"
                        style={{ backgroundColor: step.color }}
                      />
                      <span className="text-[#212121]/65 text-sm leading-relaxed">{detail}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-gray-200 pt-4">
                  <div
                    className="text-xs font-semibold"
                    style={{ color: step.color }}
                  >
                    {step.result}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-[#0D47A1]/5 to-[#00897B]/5 border border-[#0D47A1]/15 rounded-2xl p-8 max-w-2xl mx-auto text-center mb-10">
          <p className="text-[#212121]/75 text-base leading-relaxed">
            “Durante toda essa jornada você tem Dr. Mateus e toda a sua equipe ao seu lado.
            Conte conosco durante todo o processo.”
          </p>
        </div>

        <div className="text-center">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
          >
            <MessageCircle size={18} />
            MARCAR MINHA AVALIAÇÃO INICIAL
          </a>
        </div>

      </div>
    </section>
  );
}
